export type Idea = {
  id: string;
  title: string;
  body: string;
  votes: number;
  createdAt: number;
  voted?: boolean;
};

const VOTER_KEY = "meetingly_voter";

export function isFeedbackPath(): boolean {
  return location.pathname === "/featurerequests" || location.pathname === "/featurerequests/";
}

export function voterId(): string {
  let id = localStorage.getItem(VOTER_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VOTER_KEY, id);
  }
  return id;
}

export async function fetchIdeas(): Promise<Idea[]> {
  const res = await fetch(`/api/feedback?voter=${encodeURIComponent(voterId())}`);
  if (!res.ok) throw new Error("Could not load ideas");
  const body = (await res.json()) as { ideas: Idea[] };
  return body.ideas;
}

export async function postIdea(title: string, body: string): Promise<Idea> {
  const res = await fetch("/api/feedback", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ title, body }),
  });
  const json = (await res.json()) as { idea?: Idea; error?: string };
  if (!res.ok || !json.idea) throw new Error(json.error || "Could not post");
  return json.idea;
}

export async function voteIdea(id: string): Promise<void> {
  const res = await fetch(`/api/feedback/${id}/vote`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ voter: voterId() }),
  });
  if (!res.ok) throw new Error("Could not vote");
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

export function feedbackHtml(
  utcLabel: string,
  ideas: Idea[],
  loading: boolean,
  error: string,
): string {
  return `
    <div class="app-shell legal-shell">
      <header class="header glass">
        <a class="brand" href="/">
          <img class="brand-logo" src="/logo.jpg" alt="" />
          Meetingly
        </a>
        <div class="header-right">
          <div class="live-utc mono"><span class="pulse"></span>${utcLabel}</div>
          <a class="btn-ghost" href="/privacy">Privacy</a>
          <a class="btn-ghost" href="/"><span class="btn-label">Back to planner</span><span class="btn-short">Back</span></a>
        </div>
      </header>
      <article class="legal feedback-page">
        <p class="legal-kicker mono">Public board</p>
        <h1>Feature requests</h1>
        <p>Tell us what to build next. No account. Anyone can post or upvote. Keep it useful — no personal data needed.</p>

        <form class="idea-form" data-act="noop">
          <label class="duration-label mono" for="idea-title">Idea</label>
          <input id="idea-title" type="text" maxlength="120" placeholder="e.g. Outlook calendar sign-in" required />
          <label class="duration-label mono" for="idea-body">Details (optional)</label>
          <textarea id="idea-body" maxlength="800" rows="3" placeholder="What would this help you do?"></textarea>
          <button class="btn-lime" data-act="idea-submit" type="button">Post idea</button>
        </form>
        ${error ? `<p class="search-empty">${escapeHtml(error)}</p>` : ""}
        ${
          loading
            ? `<p class="roster-note">Loading ideas…</p>`
            : ideas.length
              ? `<ul class="idea-list">${ideas
                  .map(
                    (idea) => `<li class="idea-card glass">
                      <button class="vote-btn ${idea.voted ? "is-on" : ""}" data-act="idea-vote" data-id="${escapeHtml(idea.id)}" aria-label="Upvote ${escapeHtml(idea.title)}" ${idea.voted ? "disabled" : ""}>
                        <span aria-hidden="true">▲</span>
                        <span>${idea.votes}</span>
                      </button>
                      <div>
                        <h2>${escapeHtml(idea.title)}</h2>
                        ${idea.body ? `<p>${escapeHtml(idea.body)}</p>` : ""}
                      </div>
                    </li>`,
                  )
                  .join("")}</ul>`
              : `<p class="roster-note">No ideas yet. Be the first.</p>`
        }
      </article>
    </div>
  `;
}
